import React from 'react';

import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';

interface NewBadgeProps {
	/**
	 * Render the compact dot variant (used on the collapsed rail and as an
	 * accordion roll-up indicator). When false, renders the "New" text pill.
	 */
	dot?: boolean;
}

/**
 * Soft, brand-aligned discovery badge. Intentionally NOT error-red — it signals
 * "newly available", not "attention required". Driven by `useIsFeatureNew`.
 */
const NewBadge: React.FC<NewBadgeProps> = ({ dot = false }) => {
	const theme = useTheme();
	const gradient = `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`;

	if (dot) {
		return (
			<Box
				component="span"
				aria-hidden
				sx={{
					width: 8,
					height: 8,
					borderRadius: '50%',
					flexShrink: 0,
					background: gradient,
					boxShadow: `0 0 0 2px ${alpha(theme.palette.info.main, 0.25)}, 0 0 8px ${alpha(
						theme.palette.info.main,
						0.7
					)}`
				}}
			/>
		);
	}

	return (
		<Box
			component="span"
			sx={{
				px: 0.75,
				py: '1px',
				borderRadius: 99,
				fontSize: 10,
				fontWeight: 700,
				lineHeight: 1.6,
				letterSpacing: 0.4,
				textTransform: 'uppercase',
				whiteSpace: 'nowrap',
				color: theme.palette.getContrastText(theme.palette.primary.main),
				background: gradient,
				boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}, inset 0 1px 0 ${alpha('#ffffff', 0.3)}`
			}}
		>
			New
		</Box>
	);
};

export default NewBadge;
